/**
 * Drive importability rules - mirror scripts/routes/google-drive-import.js
 * `notImportableReason`. Frontend gates Import UI; backend remains the enforcer.
 */

const GOOGLE_DOCS_MIME = 'application/vnd.google-apps.document'

const IMPORTABLE_TEXT_MIMES = new Set([
  'text/plain',
  'text/markdown',
  'text/html',
  'text/csv',
])

/** Returns null if importable, or a human-readable block reason. */
export function driveImportBlockReason(mimeType: string | undefined | null): string | null {
  if (!mimeType) return null
  if (mimeType === GOOGLE_DOCS_MIME) return null
  if (IMPORTABLE_TEXT_MIMES.has(mimeType)) return null
  if (mimeType === 'application/vnd.google-apps.folder') {
    return 'Folders cannot be imported as documents.'
  }
  if (mimeType === 'application/vnd.google-apps.shortcut') {
    return 'Resolve the shortcut target before importing.'
  }
  if (mimeType.startsWith('application/vnd.google-apps.')) {
    const kind = mimeType.replace('application/vnd.google-apps.', '')
    return `Google ${kind.charAt(0).toUpperCase() + kind.slice(1)} files cannot be imported as text. Open them directly in Google Drive.`
  }
  if (mimeType === 'application/pdf') {
    return 'PDFs cannot be imported as text documents. Open the PDF directly in Google Drive to preview it.'
  }
  if (mimeType.startsWith('image/')) {
    return 'Image files cannot be imported as text documents.'
  }
  if (mimeType.startsWith('video/') || mimeType.startsWith('audio/')) {
    return 'Media files cannot be imported as text documents.'
  }
  if (mimeType.startsWith('application/')) {
    return `Binary file (${mimeType}) cannot be imported as a text document.`
  }
  return null
}

export function isDriveFileImportable(mimeType: string | undefined | null): boolean {
  return driveImportBlockReason(mimeType) == null
}

/** Mimes Drive stores for uploaded notes. Google has no third-party embed for these. */
const NATIVE_TEXT_PREVIEW_MIMES = new Set([
  'text/plain',
  'text/markdown',
  'text/x-markdown',
  'application/markdown',
])

const NATIVE_TEXT_PREVIEW_NAME_RE = /\.(md|markdown|mdx|txt)$/i

/**
 * True when Maater should fetch file bytes and render markdown/plain text.
 * Native Google Docs/Sheets still use `getDrivePreviewUrl` iframes.
 * Mirror: `scripts/utils/drive-native-text-preview.js`
 */
export function isDriveNativeTextPreview(
  mimeType: string | undefined | null,
  name?: string | null,
): boolean {
  const mime = (mimeType || '').toLowerCase()
  if (mime.startsWith('application/vnd.google-apps.')) return false
  if (NATIVE_TEXT_PREVIEW_MIMES.has(mime)) return true
  return NATIVE_TEXT_PREVIEW_NAME_RE.test(name || '')
}

const IMAGE_PREVIEW_MIMES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/svg+xml',
])

const IMAGE_PREVIEW_NAME_RE = /\.(jpe?g|png|gif|webp|bmp|svg)$/i

/**
 * True when Maater should proxy file bytes and render an <img>.
 * Mirror: `scripts/utils/drive-image-preview.js`
 */
export function isDriveImagePreview(
  mimeType: string | undefined | null,
  name?: string | null,
): boolean {
  const mime = (mimeType || '').toLowerCase()
  if (mime.startsWith('application/vnd.google-apps.')) return false
  if (/image\/(heic|heif|tiff)/.test(mime)) return false
  if (IMAGE_PREVIEW_MIMES.has(mime)) return true
  return IMAGE_PREVIEW_NAME_RE.test(name || '')
}

/**
 * Mime to use for import/preview decisions when the row may be a shortcut.
 * Prefer the target mime when present.
 */
export function driveEffectiveMime(
  mimeType: string | undefined | null,
  shortcutTargetMime?: string | null,
): string {
  if (mimeType === 'application/vnd.google-apps.shortcut' && shortcutTargetMime) {
    return shortcutTargetMime
  }
  return mimeType || ''
}
